import { Card, Col, DatePicker, Empty, Row, Statistic, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { financeApi, type StatementLine } from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: string | number) => Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const lineColumns = (label: string): TableProps<StatementLine>['columns'] => [
  { title: 'Code', dataIndex: 'accountCode', width: 90, render: (v: string | null) => v ?? '—' },
  { title: label, dataIndex: 'accountName' },
  {
    title: 'Amount',
    dataIndex: 'amount',
    width: 160,
    align: 'right',
    render: (v: string) => <Typography.Text strong>₨ {PKR(v)}</Typography.Text>,
  },
];

export function FinanceIncomeExpenditurePage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };

  const ie = useQuery({
    queryKey: ['finance', 'income-expenditure', params],
    queryFn: () => financeApi.incomeExpenditure(params.from, params.to),
  });

  const data = ie.data;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Finance / Reports"
        title="Income & Expenditure"
        description="Period earnings vs operating costs. Net surplus/deficit at the bottom matches the balance sheet's retained earnings movement."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/income-expenditure.xlsx',
              params,
              fileName: `income-expenditure-${params.from}-to-${params.to}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
              allowClear={false}
            />
          </Col>
        </Row>
      </Card>

      {data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={24} md={8}>
              <Statistic title="Total income" value={`₨ ${PKR(data.totalIncome)}`} styles={{ content: { color: '#389e0d' } }} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Total expense" value={`₨ ${PKR(data.totalExpense)}`} styles={{ content: { color: '#cf1322' } }} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic
                title={Number(data.netSurplus) >= 0 ? 'Net surplus' : 'Net deficit'}
                value={`₨ ${PKR(data.netSurplus)}`}
                styles={{ content: { color: Number(data.netSurplus) >= 0 ? '#389e0d' : '#cf1322' } }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title={<Typography.Text strong>Income</Typography.Text>}>
            {data && data.incomeLines.length === 0 ? (
              <Empty description="No income recorded in this period" />
            ) : (
              <Table<StatementLine>
                rowKey={(r) => `${r.accountCode}-${r.accountName}`}
                dataSource={data?.incomeLines ?? []}
                loading={ie.isFetching}
                columns={lineColumns('Account')}
                pagination={false}
                size="small"
                summary={() =>
                  data && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} />
                      <Table.Summary.Cell index={1}>
                        <Typography.Text strong>Total income</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Typography.Text strong style={{ color: '#389e0d' }}>
                          ₨ {PKR(data.totalIncome)}
                        </Typography.Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )
                }
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={<Typography.Text strong>Expenses</Typography.Text>}>
            {data && data.expenseLines.length === 0 ? (
              <Empty description="No expenses recorded in this period" />
            ) : (
              <Table<StatementLine>
                rowKey={(r) => `${r.accountCode}-${r.accountName}`}
                dataSource={data?.expenseLines ?? []}
                loading={ie.isFetching}
                columns={lineColumns('Account')}
                pagination={false}
                size="small"
                summary={() =>
                  data && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} />
                      <Table.Summary.Cell index={1}>
                        <Typography.Text strong>Total expense</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Typography.Text strong style={{ color: '#cf1322' }}>
                          ₨ {PKR(data.totalExpense)}
                        </Typography.Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )
                }
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
